import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiClock, FiCamera, FiAlertTriangle, FiCheckCircle, FiNavigation } = FiIcons;

function GuidePage() {
  const entrances = [
    {
      name: 'Morning Side (Suriyakanda)',
      distance: '15 km from Yaman',
      difficulty: 'Difficult (4x4 recommended)',
      highlights: ['Cloud Forest & Mist', 'Endemic Lizards', 'Sunrise Views', 'Exclusive Experience'],
      crowdLevel: 'Low',
      bestTime: '6:00 AM - 10:00 AM',
      description: 'The most pristine and exclusive entrance offering untouched rainforest experience with endemic species and breathtaking sunrise views over the Rakwana mountains.',
      color: 'emerald'
    },
    {
      name: 'Pitadeniya (Deniyaya)',
      distance: '45 km from Yaman',
      difficulty: 'Moderate',
      highlights: ['Waterfalls', 'Hanging Bridge', 'Guided Trails', 'Research Station'],
      crowdLevel: 'Moderate',
      bestTime: '7:00 AM - 4:00 PM',
      description: 'Popular entrance with well-maintained trails, visitor facilities, and spectacular waterfalls. Good for first-time visitors.',
      color: 'blue'
    },
    {
      name: 'Kudawa (Weddagala)',
      distance: '65 km from Yaman',
      difficulty: 'Easy to Moderate',
      highlights: ['Birdwatching', 'Research Center', 'Accessible Trails', 'Educational Tours'],
      crowdLevel: 'High',
      bestTime: '6:30 AM - 5:00 PM',
      description: 'Most accessible entrance with excellent facilities and guided tours. Perfect for families and educational visits.',
      color: 'yellow'
    }
  ];

  const trekTips = [
    {
      icon: FiClock,
      title: 'Best Time to Visit',
      tips: [
        'Early morning (5:30 AM - 10:00 AM) for best wildlife activity',
        'Dry season (December - April) for easier access',
        'Avoi afternoons during monsoon season'
      ]
    },
    {
      icon: FiCamera,
      title: 'What to Bring',
      tips: [
        'Waterproof camera bag and phone case',
        'Sturdy hiking boots with good grip',
        'Light rain jacket (even in dry season)',
        'Insect repellent and sunscreen',
        'Plenty of water and energy snacks'
      ]
    },
    {
      icon: FiAlertTriangle,
      title: 'Safety Guidelines',
      tips: [
        'Always hire a certified local guide',
        'Stay on marked trails at all times',
        'Inform someone of your planned route',
        'Carry a whistle for emergencies',
        'Check weather conditions before departure'
      ]
    }
  ];

  const wildlife = [
    {
      name: 'Purple-faced Langur',
      rarity: 'Endemic',
      bestSpotted: 'Early morning canopy',
      image: 'https://images.unsplash.com/photo-1564349683136-77e08dba1ef7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
    },
    {
      name: 'Sri Lanka Blue Magpie',
      rarity: 'Endemic',
      bestSpotted: 'Mid-canopy, morning',
      image: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
    },
    {
      name: 'Green Pit Viper',
      rarity: 'Endemic',
      bestSpotted: 'Tree branches, evening',
      image: 'https://images.unsplash.com/photo-1516989080801-8d4dfe1e4aa0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6">
            Sinharaja <span className="gradient-text">Guide</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Your comprehensive guide to exploring the UNESCO World Heritage Sinharaja Forest Reserve. 
            Discover the differences between entrances and plan your perfect rainforest adventure.
          </p>
        </motion.div>

        {/* Entrance Comparison */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-serif font-bold text-white text-center mb-12">
            Choose Your <span className="gradient-text">Entrance</span>
          </h2>

          <div className="grid lg:grid-cols-3 gap-8">
            {entrances.map((entrance, index) => (
              <motion.div
                key={entrance.name}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ scale: 1.03, transition: { duration: 0.3 } }}
                className={`glass-morphism rounded-2xl p-6 hover:glass-morphism-dark transition-all duration-300 ${
                  entrance.name.includes('Morning Side') ? 'border-2 border-emerald-400' : ''
                }`}
              >
                {entrance.name.includes('Morning Side') && (
                  <div className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">
                    CLOSEST TO YAMAN
                  </div>
                )}
                
                <h3 className="text-xl font-serif font-bold text-white mb-4">{entrance.name}</h3>
                <p className="text-gray-300 text-sm mb-6 leading-relaxed">{entrance.description}</p>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Distance</span>
                    <span className="text-white font-medium">{entrance.distance}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Difficulty</span>
                    <span className="text-white font-medium">{entrance.difficulty}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Crowd Level</span>
                    <span className={`font-medium ${
                      entrance.crowdLevel === 'Low' ? 'text-green-400' :
                      entrance.crowdLevel === 'Moderate' ? 'text-yellow-400' : 'text-red-400'
                    }`}>{entrance.crowdLevel}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Best Time</span>
                    <span className="text-white font-medium">{entrance.bestTime}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-white">Highlights:</h4>
                  {entrance.highlights.map((highlight) => (
                    <div key={highlight} className="flex items-center space-x-2 text-sm">
                      <SafeIcon icon={FiCheckCircle} className="w-3 h-3 text-emerald-400" />
                      <span className="text-gray-300">{highlight}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Trek Tips */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-serif font-bold text-white text-center mb-12">
            Essential <span className="gradient-text">Trek Tips</span>
          </h2>

          <div className="grid lg:grid-cols-3 gap-8">
            {trekTips.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="glass-morphism rounded-2xl p-6"
              >
                <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mb-6">
                  <SafeIcon icon={category.icon} className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{category.title}</h3>
                <ul className="space-y-2">
                  {category.tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm text-gray-300">
                      <SafeIcon icon={FiCheckCircle} className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Wildlife Spotting */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-serif font-bold text-white text-center mb-12">
            Endemic <span className="gradient-text">Wildlife</span>
          </h2>

          <div className="grid lg:grid-cols-3 gap-8">
            {wildlife.map((animal, index) => (
              <motion.div
                key={animal.name}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                whileHover={{ scale: 1.03, transition: { duration: 0.3 } }}
                className="glass-morphism rounded-2xl overflow-hidden hover:glass-morphism-dark transition-all duration-300"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={animal.image}
                    alt={animal.name}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-white mb-2">{animal.name}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Status</span>
                      <span className="text-emerald-400 font-medium">{animal.rarity}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Best Spotted</span>
                      <span className="text-white">{animal.bestSpotted}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="glass-morphism rounded-2xl p-8 text-center"
        >
          <SafeIcon icon={FiNavigation} className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
          <h3 className="text-2xl font-serif font-bold text-white mb-4">
            Need a Local Guide?
          </h3>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            We can connect you with certified local guides who know the forest intimately. 
            Get the most out of your Sinharaja experience with expert guidance.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/94771234567?text=Hi! I need help arranging a guide for Sinharaja Morning Side."
              target="_blank"
              rel="noopener noreferrer"
              className="liquid-button bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300 flex items-center justify-center space-x-2"
            >
              <SafeIcon icon={FiNavigation} className="w-5 h-5" />
              <span>Arrange Guide</span>
            </a>
            <a
              href="tel:+94771234567"
              className="border-2 border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300"
            >
              Call for Info
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default GuidePage;